import Image, { StaticImageData } from 'next/image';
import React, { FC, useEffect } from 'react';
import clsx from 'clsx';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  animationContainer,
  fadeInLeft,
  fadeInRight,
  fadeInUp,
} from './Animations';

export interface TopSectionProps {
  index: number;
  title?: string;
  subtitle?: string;

  image?: StaticImageData;
  children?: React.ReactNode;
}

const FeatureSection: FC<TopSectionProps> = ({
  index,
  image,
  title,
  children,
  subtitle,
}) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: [0.25],
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const direction = index % 2 === 0 ? 'right' : 'left';

  return (
    <motion.section
      id={`feature-${index}`}
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={animationContainer}
    >
      <div className="flex flex-col-reverse px-6 lg:grid lg:grid-cols-12 lg:items-center lg:gap-x-6">
        <div
          className={clsx(
            index % 2 !== 0
              ? 'lg:col-start-7 xl:col-start-6'
              : 'lg:col-start-1',
            'flex-auto lg:col-span-6 lg:row-start-1 xl:col-span-7'
          )}
        >
          <motion.h2
            className="mb-6 text-2xl font-extrabold leading-normal tracking-wide md:text-5xl"
            variants={fadeInUp}
          >
            {title}
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-gray-dark text-lg md:text-2xl lg:max-w-lg"
          >
            {subtitle}
          </motion.p>

          <motion.div variants={fadeInUp} className="mt-6">
            {children}
          </motion.div>
        </div>

        <div
          className={clsx(
            index % 2 !== 0 ? 'lg:col-start-1' : 'lg:col-start-8',
            'lg:col-span-7 lg:row-start-1 lg:mt-0 xl:col-span-5'
          )}
        >
          <motion.div
            variants={direction === 'right' ? fadeInRight : fadeInLeft}
            className="relative min-h-[320px] md:min-h-[525px]"
          >
            <Image src={image} alt={title} />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default FeatureSection;
